import React, { useMemo, useRef } from 'react'

import { Section, SectionHeading } from '../../../../components/ui'
import { BeforeAfter } from '../../../../components/before-after'
import {
  FRAME_H,
  FRAME_W,
  SKELETON_FRAMES,
  captionLines,
  framesFrom,
  relativeTime,
  useNearViewport,
  useRecentWashes
} from './feed'

import './recent-work.css'

// SLOT 7.4 - RECENT WORK. Design direction §7.4.
// Objection it answers: "is this real, and are these people any good?"
//
// --stone band. A row of portrait photographs of washes this business has
// actually done, each captioned with the real service and the real London
// place. That caption is the entire argument of the section. A bought
// photograph of a gleaming car cannot carry it, which is why there is no
// stock photography anywhere near this slot and why the caption is never
// decorated, abbreviated or replaced with a rating.
//
// WHAT THE PHOTOGRAPHS ARE OF, which is the point the first build missed.
// They are not pictures of clean cars. Five of the nine curated frames are
// work happening and three more are the same car twice: snow foam sitting on a
// Mercedes outside a brick terrace, a gloved hand on the spokes of an alloy, a
// lance in use beside an open van, an engine bay before and after, a microfibre
// on a panel. That is a photograph of
// competence, and competence is what a person pays £125.93 for. The heading
// and the intro now say so in the words a valeter would use, because the
// alternative is the kind of sentence that would read identically on a dog
// walking app. Every noun in the intro is in the alt text somebody wrote while
// looking at the frame (./showcase.js, mirrored from
// Order::LiveFeed::SHOWCASE_TILES): nothing about the method is invented, and
// nothing claims a step we cannot evidence.
//
// THE MECHANISM IS THE HERO'S, DELIBERATELY. The strip below is the same
// hand-built scroll-snap filmstrip as sections/hero/: a flex row inside an
// `overflow-x: auto` box, `scroll-snap-type: x mandatory` on the row and
// `scroll-snap-align: start` on each frame, the scrollbar hidden and the
// half-visible next frame doing the work an arrow button would otherwise do.
// Same gesture, same keyboard behaviour, same 1px framed photograph with its
// caption underneath, so the page teaches one interaction and not two. No
// carousel library (react-slick is ~90 KB of a 250 KB home chunk) and, unlike
// the component this replaces, no MUI arrow IconButtons either.
//
// The two strips are close enough that the mechanism wants to be a primitive.
// It is not one yet: that means editing primitives.css, which this slot is
// not allowed to touch. See the note at the top of ./recent-work.css.
//
// FOUR ACROSS AT 1440, NOT FIVE. Five frames put each photograph at 200px
// wide, which is a thumbnail: foam reads as a white smear and the gloved hand
// on the alloy is gone. Four puts them at 256px, which is the smallest size at
// which the work in them is legible, and it still leaves 32px of a fifth
// showing so the row obviously moves. The phone is untouched at 232px, which
// is the hero's number to the pixel.
//
// WHAT IT HAD TO SURVIVE. The feed is real data and real data is sometimes
// thin or old; ./feed.js documents exactly why (a four-month completion bug,
// fixed 4 Oct 2026, with a backlog still processing) and holds the rule that
// comes out of it: the strip earns timestamps only when at least four real
// washes landed inside 48 hours, and then only on the tiles that are
// themselves that recent.
//
// The consequence for this file is that THE COPY DOES NOT MOVE. The old
// component swapped its heading and its subtitle when the feed went quiet,
// which is two different claims and a reflow mid-page when the fetch lands.
// The heading, the intro and the note below are all true of a strip whose
// newest wash is two hours old and of one whose newest is four months old, so
// the only difference the data makes is whether a caption ends in "2h ago".
// The note under the strip is worded as a RULE ("newest first, from any wash
// we have photographed, alongside a handful we picked ourselves") rather than
// as a count, for exactly that reason: a count would be a different sentence
// on a quiet day. There is no LIVE badge: DESIGN.md §3 forbids badges, and a
// pulsing dot was making a promise this data cannot always keep.
//
// THE NOTE UNDER THE STRIP IS THE OTHER HALF OF THE SECTION, and it is new.
// A central London customer looking at photographs of other people's cars
// outside other people's houses has one question nothing on this page
// answered: does my car end up here, with my plate and my front door in shot.
// The answer is written down in app/services/order_feed_photo_scrubber.rb and
// it is a good one. GATE 1 of the judge prompt marks a photograph unsafe if it
// shows "Human faces", "House numbers, street signs, or address-identifying
// text" or "Name badges", and such a photograph is never published; the edit
// prompt that follows replaces any readable number plate with a blank one
// before anything is published at all. Saying so costs three lines and
// disposes of an objection that would otherwise end the visit.
//
// It also hands over. The last sentence points at §7.6, which is the other
// half of this argument: photographs are what the work looks like, and §7.6 is
// what we know about the person who did it and took them. Those photographs
// are uploaded by the assigned operator in the same call that marks the wash
// done (app/services/order/complete_service.rb: the order must already be
// `agent_id == @agent.id`), so "photographed by the washer" in the heading is
// a fact about the code path and not a turn of phrase.
//
// WHAT DELETING THE OLD COMPONENT LEAVES BEHIND: pages/home/live-wash-feed/
// is now unreferenced. Its styles.js is 200 lines of raw hex and rgba(),
// the old brand green among them, so it cannot be moved onto the tokens and
// should simply go.
// THE BEFORE-AND-AFTERS LEAD THE SECTION, 5 OCT 2026. Three real jobs as
// drag-to-compare frames, each with its service and a "Book this" link
// (components/before-after). Two of the strip's split frames are the same
// cars (06 the Audi, 09 the engine bay), so the strip drops them rather than
// show the same photograph twice, smaller, a scroll below.
const SHOWN_AS_PAIRS = ['gallery-image-06', 'gallery-image-09']

const shownAsPair = frame => {
  const file = String(frame.src || '').split('/').pop()
  return SHOWN_AS_PAIRS.some(name => file.startsWith(name))
}

export const RecentWork = () => {
  // The strip itself is the sentinel the observer watches. `<Section>` is a
  // plain function component, so it cannot take a ref, and there is no reason
  // to make the shared primitive forward one for this.
  const stripRef = useRef(null)
  const near = useNearViewport(stripRef)
  const { pending, items } = useRecentWashes(near)

  // One timestamp reading for the whole render. There is no ticking clock:
  // DESIGN.md §3 forbids countdowns, and a caption that rewrites itself while
  // you are reading it is the same idea wearing a hat.
  const { frames, isLive } = useMemo(() => {
    if (pending) return { frames: [], isLive: false }
    const built = framesFrom(items)
    return { ...built, frames: built.frames.filter(frame => !shownAsPair(frame)) }
  }, [pending, items])

  return (
    <Section background="stone" className="recent-work">
      {/* Two lines of grey at 1440, measured, not guessed: a third line was
          spent telling the reader that the wash and the postcode are under
          each photograph, which the photographs then do themselves. */}
      {/* 4 Nov 2026 (Barry via Robin): the pictures stay, "snow foam" goes.
          The line names what is in the photographs without naming a product
          or a stage of the wash. */}
      <SectionHeading intro="Suds on the paint, a gloved hand on an alloy, an engine bay half done: real cars on real London streets.">
        Fresh from London’s streets
      </SectionHeading>

      <div className="rw-pairs">
        <BeforeAfter source="before_after_home" />
      </div>

      <div
        className="rw-strip"
        ref={stripRef}
        tabIndex={0}
        role="group"
        aria-label="Images of washes we have done"
        aria-busy={pending || undefined}>
        {pending
          ? // Four empty frames, which is what a 1440 row holds, so the strip
            // is exactly as tall before the photographs arrive as after them.
            // Nothing below this section moves when the fetch lands.
            Array.from({ length: SKELETON_FRAMES }, (unused, index) => (
              <div className="rw-frame" key={`pending-${index}`} aria-hidden="true">
                <div className="rw-frame-box" />
                <div className="rw-frame-caption-box" />
              </div>
            ))
          : frames.map(frame => {
              // A time only when the section has earned one AND this
              // photograph is itself inside the 48 hour window.
              const time = isLive ? relativeTime(frame.completedAt) : null
              const { lead, clauses } = captionLines(frame, time)
              return (
                <figure className="rw-frame" key={frame.key}>
                  <img
                    className="rw-frame-img"
                    src={frame.src}
                    alt={frame.alt}
                    width={FRAME_W}
                    height={FRAME_H}
                    loading="lazy"
                    decoding="async"
                  />
                  <figcaption className="rw-frame-caption">
                    <span className="rw-frame-lead">{lead}</span>
                    {clauses.length > 0 && (
                      <span className="rw-frame-clauses">
                        {clauses.map(clause => (
                          <span className="rw-frame-clause" key={clause}>
                            {clause}
                          </span>
                        ))}
                      </span>
                    )}
                  </figcaption>
                </figure>
              )
            })}
      </div>

      {/* Under the strip, never over a photograph. Three facts, in the order
          a nervous person needs them: what the row is, what happens to your
          car's photograph, and where to read about the person who took it. */}
      <p className="rw-note">Real cars, real results, photographed by our valeters on the job.</p>
    </Section>
  )
}
